import { useCallback } from 'react'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useApolloClient, useMutation } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

import { removeDeletedNote } from './notesCache'
import type { CollaborationNote } from './notesQueries'
import type { NoteSubject } from './noteSubject'
import { useScopedNoteWrite } from './useScopedNoteWrite'

export const DeleteCollaborationNoteMutation = graphql(`
  mutation DeleteCollaborationNote($input: DeleteCollaborationNoteInput!) {
    deleteCollaborationNote(input: $input) {
      __typename
      ... on DeleteCollaborationNoteSuccess {
        noteId
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

export type DeleteNoteFailure = 'permission' | 'request'
export type DeleteNoteResult =
  | { ok: true }
  | { ok: false; reason: DeleteNoteFailure }

type ConfirmedDelete = { subject: NoteSubject; noteId: string }

export function useDeleteNote(subject: NoteSubject): {
  submit: (
    note: CollaborationNote,
    onConfirmed?: () => void,
  ) => Promise<DeleteNoteResult>
  pending: boolean
} {
  const client = useApolloClient()
  const applyConfirmed = useCallback(
    ({ subject: confirmedSubject, noteId }: ConfirmedDelete): void =>
      removeDeletedNote(client.cache, confirmedSubject, noteId),
    [client.cache],
  )
  const { scope, scopeKey, isCurrent, pending, run, keepConfirmed } =
    useScopedNoteWrite(subject, applyConfirmed)
  const [mutate] = useMutation(DeleteCollaborationNoteMutation, {
    errorPolicy: 'all',
  })

  const submit = useCallback(
    async (
      note: CollaborationNote,
      onConfirmed?: () => void,
    ): Promise<DeleteNoteResult> => {
      if (!scope || !scopeKey || !note.allowedActions.includes('deleteNote')) {
        return { ok: false, reason: 'permission' }
      }

      const outcome = await run(
        scopeKey,
        async (): Promise<DeleteNoteResult> => {
          try {
            const response = await mutate({
              variables: { input: { noteId: note.id } },
              fetchPolicy: 'no-cache',
            })
            const payload = response.data?.deleteCollaborationNote
            if (
              payload?.__typename === 'DeleteCollaborationNoteSuccess' &&
              payload.noteId === note.id
            ) {
              if (isCurrent(scopeKey)) {
                onConfirmed?.()
                removeDeletedNote(client.cache, scope.subject, payload.noteId)
              } else {
                keepConfirmed(scopeKey, {
                  subject: scope.subject,
                  noteId: payload.noteId,
                })
              }
              return { ok: true }
            }
            if (
              CombinedGraphQLErrors.is(response.error) &&
              response.error.errors.some(
                (error) => error.extensions?.code === 'FORBIDDEN',
              )
            ) {
              return { ok: false, reason: 'permission' }
            }
            return { ok: false, reason: 'request' }
          } catch {
            return { ok: false, reason: 'request' }
          }
        },
      )
      return outcome.ran ? outcome.result : { ok: false, reason: 'request' }
    },
    [client.cache, isCurrent, keepConfirmed, mutate, run, scope, scopeKey],
  )

  return { submit, pending }
}
